import React, { useState } from 'react';
import { Plus, Settings } from 'lucide-react';
import { CopyButton, DeleteButton } from './shared';
import { useLocale } from '../../contexts/LocaleContext';

const EMPTY_LINK_FORM = { name: '', url: '' };

export default function PaymentLinksPanel({ links, onSave, onDelete }) {
  const { t } = useLocale();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_LINK_FORM);
  const [editId, setEditId] = useState(null);

  const resetForm = () => {
    setForm(EMPTY_LINK_FORM);
    setEditId(null);
    setShowForm(false);
  };

  const startEdit = (l) => {
    setForm({ name: l.name, url: l.url });
    setEditId(l.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-2">
      {links.map((l) => (
        <div
          key={l.id}
          className="flex items-center gap-3 px-3 py-2.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--text-primary)]">{l.name}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[var(--accent-primary)] hover:underline truncate"
              >
                {l.url}
              </a>
              <CopyButton text={l.url} />
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => startEdit(l)}
              className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-smooth"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
            <DeleteButton onDelete={() => onDelete(l.id)} />
          </div>
        </div>
      ))}

      {showForm && (
        <div className="border border-[var(--accent-primary)] rounded-lg p-3 bg-[var(--accent-primary)]/5 space-y-2">
          <p className="text-xs font-semibold text-[var(--text-primary)]">
            {editId ? t('plugins.editLabel') : t('plugins.newLabel')} {t('plugins.paymentLink')}
          </p>
          <input
            placeholder={t('plugins.linkNamePlaceholder')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)] w-full"
          />
          <input
            placeholder="URL"
            type="url"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)] w-full"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { onSave(editId, form); resetForm(); }}
              className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs transition-smooth"
            >
              {editId ? t('plugins.saveLabel') : t('plugins.addLabel')}
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth"
            >
              {t('plugins.cancelLabel')}
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('plugins.addPaymentLink')}
        </button>
      )}
    </div>
  );
}